import { holidayQueryKeys } from "@/api/query-keys/holidays"
import { Holiday, holidayTypeMap } from "@/api/types/holidays"
import {
  HOLIDAY_STORAGE_KEY,
  useHolidayFilters,
} from "@/hooks/filters/useHolidayFilters"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import EditHolidayModal from "@/components/modals/holidays/EditHolidayModal"
import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { ColumnDef } from "@tanstack/react-table"
import { useState } from "react"
import {
  Badge,
  createActionsColumn,
  createCustomColumn,
  createSortableColumn,
  createTableSearchSchema,
  DataTable,
  formatDateToDutch,
  selectColumn,
  TABLE_PAGE_SIZE,
  usePaginatedTableWithUrlState,
  MultiLevelDropdownMenu,
  useExportMutation,
} from "toptaal-shared"

export const Route = createFileRoute("/beheer/feestdagen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "holidays"),
  validateSearch: createTableSearchSchema(),
})

const ALL_COLUMN_IDS = ["id", "title", "startDate", "endDate", "type", "active"]

function RouteComponent() {
  const searchParams = useSearch({ from: "/beheer/feestdagen/" })
  const navigate = useNavigate({ from: "/beheer/feestdagen/" })
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<Holiday | null>(null)

  const { filterItems, activeFilters, queryFn } = useHolidayFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...holidayQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: HOLIDAY_STORAGE_KEY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const columns: ColumnDef<Holiday>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({ accessorKey: "title", name: "Feestdag" }),
    createCustomColumn({
      id: "startDate",
      name: "Startdatum",
      cell: ({ row }) => formatDateToDutch(row.original.startDate),
      accessorKey: "startDate",
    }),
    createCustomColumn({
      id: "endDate",
      name: "Einddatum",
      cell: ({ row }) => formatDateToDutch(row.original.endDate),
      accessorKey: "endDate",
    }),
    createCustomColumn({
      id: "type",
      name: "Type",
      cell: ({ row }) => {
        return <p>{holidayTypeMap[row.original.type]}</p>
      },
      accessorKey: "type",
    }),
    createCustomColumn({
      id: "active",
      name: "Actief",
      cell: ({ row }) => {
        return (
          <Badge variant={row.original.active ? "green" : "lightGrey"}>
            {row.original.active ? "Actief" : "Non-actief"}
          </Badge>
        )
      },
      accessorKey: "active",
    }),
    createActionsColumn([
      {
        label: "Feestdag wijzigen",
        onClick: (e, holiday) => {
          e.stopPropagation()
          setActiveRow(holiday)
          setIsEditOpen(true)
        },
      },
    ]),
  ]

  const { mutate: exportHolidays } = useExportMutation({
    exportApiPrefix: "holidays",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Feestdagen"
        data={data?.data.holidays || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={<MultiLevelDropdownMenu items={filterItems} />}
        headerRightActions={
          <TableHeaderActions
            exportAction={{
              onClick: exportHolidays,
            }}
            createAction={{
              label: "Nieuwe feestdag",
              to: "/beheer/feestdagen/aanmaken",
              entity: "holidays",
              permissions: "create",
            }}
          />
        }
      />

      {isEditOpen && activeRow && (
        <EditHolidayModal
          isEditOpen={isEditOpen}
          setIsEditOpen={setIsEditOpen}
          activeRow={activeRow}
          closeDialog={() => setIsEditOpen(false)}
        />
      )}
    </ScreenWrapper>
  )
}
